import './DigitalSection.css'
import React from 'react';
import { useAppDispatch, useAppSelector } from '../../Redux/hooks';
import { setInputValue } from '../../Redux/Reducers/briefSlice';
import { useTranslation } from 'react-i18next';
import { Button, Col, Form, Row } from 'react-bootstrap';

import { BriefJAMDTO } from '../../Business/dto/BriefJAM';
import { Section } from './Section';
import { Input } from '../Inputs/Input';
import { DigitalTargetOption, DigitalTargetPicker } from '../Inputs/DigitalTargetPicker';
import { useAutoSelectionTracker } from '../Inputs/AutoSelectionTracker';
import { DefaultInput } from '../Inputs/DefaultInput';
import { DigitalFormatOption, DigitalFormatPicker } from '../Inputs/DigitalFormatPicker';
import { DigitalContentOption, DigitalContentPicker } from '../Inputs/DigitalContentPicker';
import { DigitalDeviceOption, DigitalDevicePicker } from '../Inputs/DigitalDevicePicker';
import { formatNumberToCurrency } from '../../Business/utils/CurrencyUtils';


interface DigitalSectionProps {
    idx: number
    expanded: boolean
    onChangeSection: (sectionIdx: number) => void
    setSectionValidated: (sectionIdx: number, validated: boolean) => void
    nextButtonVisible: boolean
    nextButtonDisabled: boolean
    briefToLoad?: BriefJAMDTO
}

interface DigitalSectionInputValues {
    digitalBudget: number
    digitalTarget?: DigitalTargetOption[]
    digitalFormat?: DigitalFormatOption[]
    digitalContent?: DigitalContentOption 
    digitalDevice?: DigitalDeviceOption[]
}


export class DigitalSection extends Section<DigitalSectionProps> {
    Name = "txt_digital_section_name"

    SectionBody = (props: DigitalSectionProps) => {
        const { t } = useTranslation();
        const dispatch = useAppDispatch();

        const hasBudget = useAppSelector(state => state.brief.inputData?.hasBudget)
        const fullBudget = useAppSelector(state => state.brief.inputData?.fullBudget);
        const currency = useAppSelector(state => state.brief.inputData?.selectedCurrency)
        // eslint-disable-next-line
        const { markAsUserSelected, isUserSelected } = useAutoSelectionTracker();
        const [inputValues, setInputValues] = React.useState<DigitalSectionInputValues>({
            digitalBudget: 0,
            digitalTarget: undefined,
            digitalFormat: undefined,
            digitalContent: undefined,
            digitalDevice: undefined,
        });
        const [budgetConstraint, setBudgetConstraint] = React.useState<number>(0)

        const onFormSubmit = (event: any) => {
            event.preventDefault();
            props.onChangeSection(props.idx + 1);
        }

        React.useEffect(() => {
            setBudgetConstraint(hasBudget ? fullBudget : 0)
            const valid = (
                inputValues.digitalBudget > 0 && (!hasBudget || fullBudget > inputValues.digitalBudget) &&
                inputValues.digitalTarget !== undefined && inputValues.digitalTarget?.length !== 0 &&
                inputValues.digitalFormat !== undefined && inputValues.digitalFormat?.length !== 0 &&
                inputValues.digitalContent !== undefined &&
                inputValues.digitalDevice !== undefined && inputValues.digitalDevice?.length !== 0
            )

            props.setSectionValidated(props.idx, valid);
        }, [inputValues, hasBudget, fullBudget])
        
        const setValues = (value: any, key: string, isUserSelection: boolean = true) => {
            if (isUserSelection) {
                markAsUserSelected(key);
            }
            setInputValues({ ...inputValues, [key]: value });
            dispatch(setInputValue({ key, value }));
        }


        return (
            <Form onSubmit={onFormSubmit}>
                <Row className="briefInputRow">
                    <Col className="multipleInputsRow">
                        <Input required title={hasBudget ? t("txt_digital_budget_share") : t("txt_digital_budget")}>
                        <DefaultInput
                            onChange={(value: any) => setValues(parseFloat(value), "digitalBudget", true)}
                            type="currency"
                            iconKey="currency"
                            placeholder={hasBudget ? t("txt_digital_budget_share") : t("txt_digital_budget")}
                            value={inputValues.digitalBudget}
                        />
                        <Col className='inputConstraint'>
                            {budgetConstraint > 0 && t("txt_digital_budget_constraint") + ` ${formatNumberToCurrency(fullBudget, currency)}`}
                        </Col>
                        </Input>
                    </Col>
                    <Col className="multipleInputsRow">
                        <Input required title={t("txt_digital_target")}>
                            <DigitalTargetPicker
                                setDigitalTarget={(digitalTarget) => setValues(digitalTarget, "digitalTarget")}
                                value={inputValues.digitalTarget}
                            />
                        </Input>
                    </Col>
                </Row>
                <Row className="briefInputRow">
                    <Col className="multipleInputsRow">
                        <Input required title={t("txt_digital_format")}>
                            <DigitalFormatPicker
                                setDigitalFormat={(digitalFormat) => setValues(digitalFormat, "digitalFormat")}
                                value={inputValues.digitalFormat}
                            />
                        </Input>
                    </Col>
                </Row>
                <Row className="briefInputRow">
                    <Col className="multipleInputsRow">
                        <Input required title={t("txt_digital_content")}>
                            <DigitalContentPicker
                                setDigitalContent={(digitalContent) => setValues(digitalContent, "digitalContent")}
                                value={inputValues.digitalContent}
                            />
                        </Input>
                    </Col>
                    <Col className="multipleInputsRow">
                        <Input required title={t("txt_digital_device")}>
                            <DigitalDevicePicker
                                setDigitalDevice={(digitalDevice) => setValues(digitalDevice, "digitalDevice")}
                                value={inputValues.digitalDevice}
                            />
                        </Input>
                    </Col>
                </Row>
                {
                    props.nextButtonVisible &&
                    <Col className="text-center">
                        <Button
                            type="submit"
                            className="sectionContinueButton"
                            variant="dark"
                            disabled={props.nextButtonDisabled}>
                            {t("next_button")}
                        </Button>
                    </Col>
                }
            </Form>)
    }
}